import { h, toast, tableScroll, rowAction, confirmDialog } from '../components/dom.js';
import { renderShell, pageHead, portalFooter } from '../components/shell.js';
import { api } from '../api/client.js';
import { router } from '../router/router.js';
import { profileStateView } from './profile-state.js';
import { publicProfileCard } from './public.js';
import { profileFieldName } from './fields-catalog.js';
import { mergeContactsByOwner, type MergedContact } from './merge.js';
import { openContactCardModal } from './contact-card-modal.js';
import type { Profile } from '../api/types.js';
import { dateTimeEl } from '../lib/timezone.js';

export async function renderProfiles(): Promise<void> {
  const main = renderShell('/profiles');
  const newBtn = h('button', { class: 'btn btn-primary btn-sm' }, 'New profile');
  newBtn.addEventListener('click', async () => {
    const name = prompt('Profile name (e.g. Work, Personal):');
    if (!name) return;
    try { const p = await api.createProfile({ name }); toast('Profile created', 'ok'); router.navigate('/profiles/' + p.id); }
    catch (e) { toast((e as Error).message, 'danger'); }
  });
  main.append(pageHead('Profiles', 'Purpose-scoped views of your master identity.', newBtn));

  const grid = h('div', { class: 'grid grid-2' });
  main.append(grid, portalFooter());

  const { data: profiles } = await api.profiles();
  for (const p of profiles) grid.append(profileCard(p));
  if (!profiles.length) grid.append(h('p', { class: 'muted' }, 'No profiles yet — create one.'));
}

function profileCard(p: Profile): HTMLElement {
  const st = profileStateView(p);
  const badge = p.is_public
    ? h('span', { class: 'badge badge-ok' }, 'Public URL')
    : h('span', { class: 'badge' }, 'Private');
  const statusBadge = h('span', { class: st.statusBadgeClass }, st.statusLabel);

  // Enable/disable toggle — allowed for ALL profiles including the public one.
  // Non-destructive toggle — no confirmation.
  const toggleBtn = rowAction(st.toggleLabel, async () => {
    try {
      await api.setProfileEnabled(p.id, st.disabled); // disabled -> enable, enabled -> disable
      toast(st.enabled ? 'Profile disabled' : 'Profile enabled', 'ok');
      renderProfiles();
    } catch (e) { toast((e as Error).message, 'danger'); }
  });

  const actions = [
    viewBtn(p),
    link('Edit', '/profiles/' + p.id, true),
    link('Subscribers', '/subscribers?profile=' + encodeURIComponent(p.id)),
    toggleBtn,
  ];
  if (st.deletable) {
    const del = rowAction('Delete', async () => {
      if (!confirmDialog('Delete this profile? This permanently removes the profile and its exposure settings.')) return;
      try { await api.deleteProfile(p.id); toast('Profile deleted', 'ok'); renderProfiles(); }
      catch (e) { toast((e as Error).message, 'danger'); }
    }, { variant: 'danger' });
    actions.push(del);
  } else {
    // Public profile can never be deleted — show a disabled control w/ tooltip.
    actions.push(rowAction('Delete', () => {}, {
      disabled: true,
      title: "Public profile can't be deleted — disable it instead",
    }));
  }

  return h('div', { class: 'card' + (st.disabled ? ' is-disabled' : '') },
    h('div', { class: 'row between' }, h('h3', { style: 'margin:0' }, p.name),
      h('div', { class: 'row', style: 'gap:6px' }, statusBadge, badge)),
    h('p', {}, p.description || '—'),
    h('div', { class: 'pill-row' },
      h('span', { class: 'badge' }, `${p.exposed_field_count ?? 0} fields exposed`),
      h('span', { class: 'badge badge-ok' }, `${p.subscriber_count ?? 0} subscribers`)),
    h('div', { class: 'row-actions', style: 'margin-top:16px' }, ...actions),
  );
}

// viewBtn opens a modal previewing this profile exactly as a subscriber sees it
// (reuses the public profile card template via GET /profiles/{id}/preview).
function viewBtn(p: Profile): HTMLElement {
  return rowAction('View', () => openPreview(p));
}

async function openPreview(p: Profile): Promise<void> {
  const overlay = h('div', { class: 'modal-overlay', style: 'align-items:flex-start;padding:40px 20px;overflow:auto', onclick: () => overlay.remove() });
  const box = h('div', { style: 'max-width:520px;width:100%', onclick: (e: Event) => e.stopPropagation() },
    h('p', { class: 'label', style: 'color:#fff;margin:0 0 10px' }, `Preview — how subscribers see “${p.name}”`));
  overlay.append(box); document.body.append(overlay);
  try {
    const view = await api.previewProfile(p.id);
    // alias = the handle the server returns for this account's public URL.
    box.append(publicProfileCard(view, view.handle || p.name, { interactive: false }));
  } catch (e) {
    box.append(h('div', { class: 'card' }, h('p', { class: 'muted' }, (e as Error).message || 'Preview failed')));
  }
  const close = h('button', { class: 'btn btn-ghost btn-sm btn-on-overlay', style: 'margin-top:12px' }, 'Close');
  close.addEventListener('click', () => overlay.remove());
  box.append(h('div', { style: 'text-align:center' }, close));
}

function link(label: string, path: string, _primaryGhost = false): HTMLElement {
  const a = h('a', { class: 'btn btn-ghost btn-sm', href: '#' + path }, label);
  a.addEventListener('click', (e) => { e.preventDefault(); router.navigate(path); });
  return a;
}

// --- Profile edit + exposure matrix ---
export async function renderProfileDetail(params: Record<string, string>): Promise<void> {
  const main = renderShell('/profiles');
  const id = params.id;

  const [profile, fieldsRes, exposuresRes, subsAll, catalogRes, linksAll, contactsRes] = await Promise.all([
    api.profile(id),
    api.fields(),
    api.exposures(id),
    api.subscribers(),
    api.fieldTypes(),
    api.shareLinks(),
    api.contacts().catch(() => ({ data: [] as import('../api/types.js').Contact[] })),
  ]);
  const catalog = catalogRes.data;
  // Full profile list — needed by the subscriber editor (opened from a subscriber's
  // contact card) to offer 'add to another profile'.
  const allProfiles = (await api.profiles().catch(() => ({ data: [] as import('../api/types.js').Profile[] }))).data;
  // Owner-indexed merged contacts, so the "Who has access" list can open a
  // subscriber's full contact card (same popup as the Subscribers page).
  const contactByOwner = new Map(
    mergeContactsByOwner(contactsRes.data).map((m) => [m.ownerId, m]));

  // fieldNameCell builds the FIELD column name-block matching My Contacts:
  // friendly name primary (title=raw id), optional annotation as a muted subline,
  // with the high-sensitivity badge appended after the name. All field-derived
  // text goes through h() text nodes (no raw html interpolation) to preserve
  // XSS-safety.
  const fieldNameCell = (f: typeof fieldsRes.data[number]): HTMLElement => {
    const nm = profileFieldName(f, catalog);
    const head = h('div', { class: 'row', style: 'gap:6px;align-items:center;flex-wrap:wrap' },
      h('span', { title: nm.id }, nm.name));
    if (f.sensitivity === 'high') head.append(h('span', { class: 'badge badge-warn' }, 'high sensitivity'));
    if (!nm.subline) return head;
    return h('div', {}, head,
      h('div', { class: 'muted', style: 'font-size:.78em;opacity:.8' }, nm.subline));
  };
  const subsRes = { data: subsAll.data.filter((s) => s.profile_id === id) };
  const exposedMap = new Map(exposuresRes.data.map((e) => [e.field_id, e.exposed]));
  // exposuresRes arrives in the owner's saved position order. Build an ordered
  // working list: exposed fields first (in saved order), then the rest. This is
  // the drag-reorderable source of truth; its ARRAY ORDER becomes the position.
  const exposedOrder = new Map(exposuresRes.data.map((e, i) => [e.field_id, i]));
  const orderedFields = [...fieldsRes.data].sort((a, b) => {
    const ai = exposedOrder.has(a.id) ? exposedOrder.get(a.id)! : Number.MAX_SAFE_INTEGER;
    const bi = exposedOrder.has(b.id) ? exposedOrder.get(b.id)! : Number.MAX_SAFE_INTEGER;
    return ai - bi;
  });

  const back = h('a', { class: 'muted', href: '#/profiles', 'aria-label': 'Back', html: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>' });
  const saveBtn = h('button', { class: 'btn btn-primary btn-sm' }, 'Save exposure');

  // Inline edit of the profile's NAME and DESCRIPTION via tiny pencil icons next to
  // each. Edit is LOCAL: it changes only profiles.name / profiles.description via
  // PATCH; the profile id and its well-known slug are unchanged, so subscriptions and
  // share links (which reference the profile by id) keep working. ALL profiles are
  // editable. icon/color/is_public are preserved so a partial edit never blanks them.
  const patchMeta = async (patch: { name?: string; description?: string }): Promise<void> => {
    await api.updateProfile(profile.id, {
      name: patch.name ?? profile.name,
      description: patch.description ?? profile.description,
      icon: profile.icon, color: profile.color, is_public: profile.is_public,
    });
  };
  const pencil = (label: string): HTMLElement => h('button', {
    class: 'btn-icon', 'aria-label': label, title: label,
    style: 'background:none;border:none;cursor:pointer;padding:2px;opacity:.6;line-height:0',
    html: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>',
  });

  const titleEl = h('h1', { style: 'font-size:1.7rem;margin:0' }, profile.name);
  const descEl = h('p', { style: 'margin:0' }, profile.description || 'Choose which fields this profile exposes.');
  const titlePencil = pencil('Edit profile name');
  const descPencil = pencil('Edit profile description');

  // inlineEdit swaps the target text element for an input; Enter/blur saves (only
  // if changed), Escape cancels. On success the whole view reloads so every place
  // that shows the name/description stays consistent.
  const inlineEdit = (
    display: HTMLElement, current: string, apply: (v: string) => Promise<void>,
    opts: { required?: boolean; placeholder?: string } = {},
  ): void => {
    const input = h('input', { class: 'input', value: current, placeholder: opts.placeholder || '' }) as HTMLInputElement;
    display.replaceWith(input);
    input.focus(); input.select();
    let done = false;
    const save = async (): Promise<void> => {
      if (done) return;
      const next = input.value.trim();
      if (opts.required && !next) { input.replaceWith(display); return; } // empty name => cancel
      if (next === current) { input.replaceWith(display); return; }
      done = true;
      try { await apply(next); toast('Profile updated', 'ok'); router.navigate('/profiles/' + profile.id); }
      catch (e) { toast((e as Error).message, 'danger'); input.replaceWith(display); }
    };
    input.addEventListener('blur', save);
    input.addEventListener('keydown', (e) => {
      const k = (e as KeyboardEvent).key;
      if (k === 'Enter') input.blur();
      else if (k === 'Escape') { done = true; input.replaceWith(display); }
    });
  };
  titlePencil.addEventListener('click', () => inlineEdit(titleEl, profile.name, (v) => patchMeta({ name: v }), { required: true }));
  descPencil.addEventListener('click', () => inlineEdit(descEl, profile.description || '', (v) => patchMeta({ description: v }), { placeholder: 'Profile description' }));

  main.append(h('div', { class: 'page-head row between' },
    h('div', { class: 'row', style: 'gap:12px' }, back,
      h('div', {},
        h('div', { class: 'row', style: 'gap:6px;align-items:center' }, titleEl, titlePencil),
        h('div', { class: 'row', style: 'gap:6px;align-items:center' }, descEl, descPencil)),
      profile.is_public ? h('span', { class: 'badge badge-ok' }, 'Public URL') : h('span', { class: 'badge' }, 'Private')),
    h('div', { class: 'row', style: 'gap:8px' }, saveBtn)));

  // Exposure matrix table with toggles + drag-to-reorder. Row order == field
  // position (owner's explicit stable order); the array is rebuilt on each drop.
  const tbody = h('tbody');
  const toggleState = new Map<string, boolean>();
  let dragKey: string | null = null;

  const buildRows = (): void => {
    tbody.replaceChildren();
    for (const f of orderedFields) {
      const on = toggleState.get(f.id) ?? false;
      const btn = h('button', { class: 'toggle' + (on ? ' on' : ''), 'aria-label': on ? 'Exposed' : 'Not exposed' });
      btn.addEventListener('click', () => {
        const next = !toggleState.get(f.id);
        toggleState.set(f.id, next);
        btn.className = 'toggle' + (next ? ' on' : '');
        refreshPreview();
      });
      const grip = h('span', { class: 'drag-handle', title: 'Drag to reorder', 'aria-hidden': 'true' }, '\u22ee\u22ee');
      const valueCell = h('td', { title: 'Double-click to edit \u2014 applies to every profile that shares this field', style: 'cursor:text' }, f.value || h('span', { class: 'muted' }, '\u2014'));
      valueCell.addEventListener('dblclick', () => editSharedValue(f, valueCell));
      const tr = h('tr', { draggable: 'true' },
        h('td', { style: 'width:1%;white-space:nowrap' }, grip),
        h('td', { class: 'col-exposed' }, btn),
        h('td', {}, fieldNameCell(f)),
        valueCell) as HTMLTableRowElement;
      tr.dataset.key = f.id;
      tr.addEventListener('dragstart', (e) => {
        dragKey = f.id;
        tr.classList.add('dragging');
        if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', f.id); }
      });
      tr.addEventListener('dragend', () => { tr.classList.remove('dragging'); tbody.querySelectorAll('.drag-over-tr').forEach((el) => el.classList.remove('drag-over-tr')); dragKey = null; });
      tr.addEventListener('dragover', (e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'; tr.classList.add('drag-over-tr'); });
      tr.addEventListener('dragleave', () => tr.classList.remove('drag-over-tr'));
      tr.addEventListener('drop', (e) => {
        e.preventDefault();
        tr.classList.remove('drag-over-tr');
        if (!dragKey || dragKey === f.id) return;
        const from = orderedFields.findIndex((x) => x.id === dragKey);
        const to = orderedFields.findIndex((x) => x.id === f.id);
        if (from < 0 || to < 0) return;
        const [moved] = orderedFields.splice(from, 1);
        orderedFields.splice(to, 0, moved);
        buildRows();
      });
      tbody.append(tr);
    }
  };

  // Local preview (v1 has no server-side /preview): reflect the current toggle state.
  const previewBody = h('div', {});
  const refreshPreview = () => {
    const exposed = orderedFields.filter((f) => toggleState.get(f.id));
    previewBody.replaceChildren(...exposed.map((f) =>
      h('div', { class: 'row between', style: 'padding:6px 0;border-bottom:1px solid var(--border)' },
        h('small', { class: 'muted', title: profileFieldName(f, catalog).id }, profileFieldName(f, catalog).name), h('span', {}, f.value || ''))));
    if (!exposed.length) previewBody.replaceChildren(h('p', { class: 'muted' }, 'No fields exposed yet.'));
  };

  // Seed toggle state from the saved exposure set, then render the ordered rows.
  for (const f of orderedFields) toggleState.set(f.id, exposedMap.get(f.id) ?? false);
  buildRows();

  saveBtn.addEventListener('click', async () => {
    // Send entries in DISPLAYED order — the backend uses array index as the
    // field position (owner's explicit stable order).
    const exposures = orderedFields.map((f) => ({ field_id: f.id, exposed: toggleState.get(f.id) ?? false }));
    try {
      // Exposure change is step-up gated (API-CONTRACT §5.4). We attempt without step-up;
      // a 403 step_up_required prompts a re-auth (TODO: wire VID-Step-Up assertion).
      await api.setExposures(id, exposures);
      toast('Exposure updated — subscribers will refresh', 'ok');
    } catch (e) {
      const msg = (e as Error).message;
      toast(msg.includes('step') ? 'Step-up required — confirm with your passkey (TODO)' : msg, 'warn');
    }
  });

  main.append(h('div', { class: 'grid grid-2', style: 'align-items:start' },
    h('div', { class: 'card', style: 'padding:0' },
      h('div', { class: 'row between', style: 'padding:18px 20px 12px' },
        h('h3', { style: 'margin:0' }, 'Fields exposed'),
        h('span', { class: 'badge' }, `${fieldsRes.data.length} fields`)),
      tableScroll(h('table', { class: 'table' },
        h('thead', {}, h('tr', {}, h('th', {}, ''), h('th', { class: 'col-exposed' }, 'Exposed'), h('th', {}, 'Field'), h('th', {}, 'Value'))),
        tbody))),
    h('div', { class: 'stack' }, whoHasAccess(subsRes.data, id, contactByOwner, allProfiles, subsAll.data), activeSharedLinks(linksAll.data, id), previewCard(previewBody, refreshPreview, profile))));
  main.append(portalFooter());
}

// editSharedValue inline-edits a field's VALUE from the profile exposure table.
// Because a field instance is SHARED across every profile that exposes it, saving
// a change here mutates the value everywhere. So on save we require EXPLICIT
// confirmation (Apply/Cancel) warning about the cross-profile impact; Cancel
// reverts the cell to the original value and commits nothing.
async function editSharedValue(f: import('../api/types.js').FieldInstance, cell: HTMLElement): Promise<void> {
  if (cell.querySelector('input')) return; // already editing
  const original = f.value || '';
  const render = (v: string) => cell.replaceChildren(v ? document.createTextNode(v) : h('span', { class: 'muted' }, '\u2014'));
  const input = h('input', { class: 'input', value: original }) as HTMLInputElement;
  cell.replaceChildren(input);
  input.focus();
  input.select();
  let done = false;
  const save = async () => {
    if (done) return;
    const next = input.value;
    if (next === original) { done = true; render(original); return; }
    // EXPLICIT cross-profile confirmation. Cancel => revert, commit nothing.
    const ok = confirmDialog(
      'This value is shared. Changing it will also update this field on ALL other '
      + 'profiles that expose it.\n\nApply this change everywhere?');
    if (!ok) { done = true; render(original); return; } // reverted, unchanged
    done = true;
    try {
      const updated = await api.updateField(f.id, { value: next });
      f.value = updated.value || next;
      render(f.value);
      toast('Field updated on all profiles \u2014 subscribers refreshed', 'ok');
    } catch (e) {
      render(original); // leave unchanged on failure
      toast((e as Error).message, 'danger');
    }
  };
  input.addEventListener('blur', save);
  input.addEventListener('keydown', (e) => {
    const k = (e as KeyboardEvent).key;
    if (k === 'Enter') input.blur();
    else if (k === 'Escape') { done = true; render(original); } // cancel, unchanged
  });
}

function whoHasAccess(subs: import('../api/types.js').Subscription[], profileId: string, contactByOwner: Map<string, MergedContact>, allProfiles: import('../api/types.js').Profile[], allSubs: import('../api/types.js').Subscription[]): HTMLElement {
  const filterHref = '#/subscribers?profile=' + encodeURIComponent(profileId);
  // Show only the 10 MOST RECENT subscribers here; the full, paginated list lives
  // on the Subscribers page (pre-filtered to this profile). Sort by `since` desc
  // so "most recent" is stable regardless of server order.
  const sorted = [...subs].sort((a, b) => (b.since || '').localeCompare(a.since || ''));
  const top = sorted.slice(0, 10);
  const tbody = h('tbody');
  for (const s of top) {
    const uname = s.subscriber?.username || s.subscriber?.display_name || 'Subscriber';
    const link = h('a', { href: '#', style: 'text-decoration:none;font-weight:600' }, uname);
    link.addEventListener('click', (e) => {
      e.preventDefault();
      openContactCardModal({ contact: contactByOwner.get(s.subscriber_id || ''), username: uname, showNavToContact: true,
        subscriberSubs: allSubs.filter((x) => x.subscriber_id === s.subscriber_id && x.status === 'active'),
        profiles: allProfiles });
    });
    tbody.append(h('tr', {},
      h('td', {}, link),
      h('td', {}, s.since ? dateTimeEl(s.since) : '\u2014'),
      h('td', {}, h('span', { class: 'badge ' + (s.status === 'active' ? 'badge-ok' : 'badge-warn') }, s.status || '—')),
      h('td', {}, (s.labels || []).map((l) => h('span', { class: 'badge' }, l)))));
  }
  if (!top.length) tbody.append(h('tr', {}, h('td', { colspan: '4', class: 'muted' }, 'No subscribers yet.')));
  // "Edit" (and "See more" when truncated) both jump to the Subscribers page with
  // this profile's filter already applied, showing the full default-paginated list.
  const editBtn = h('a', { class: 'btn btn-ghost btn-sm', href: filterHref }, 'Edit');
  const card = h('div', { class: 'card', style: 'padding:0' },
    h('div', { class: 'row between', style: 'padding:18px 20px 12px' },
      h('h3', { style: 'margin:0' }, 'Who has access'), editBtn),
    tableScroll(h('table', { class: 'table' },
      h('thead', {}, h('tr', {}, h('th', {}, 'Name'), h('th', {}, 'Since'), h('th', {}, 'Status'), h('th', {}, 'Labels'))),
      tbody)));
  if (subs.length > top.length) {
    card.append(h('div', { style: 'padding:12px 20px 18px' },
      h('a', { class: 'muted', href: filterHref }, `See more (${subs.length} total) →`)));
  }
  return card;
}

function activeSharedLinks(links: import('../api/types.js').ShareLink[], profileId: string): HTMLElement {
  const filterHref = '#/links?profile=' + encodeURIComponent(profileId);
  // Only ACTIVE links for THIS profile; show the 10 most recent (created_at desc).
  // Full, paginated management lives on the Share Links page (pre-filtered here).
  const mine = links.filter((l) => l.profile_id === profileId && l.status === 'active');
  const sorted = [...mine].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  const top = sorted.slice(0, 10);
  const tbody = h('tbody');
  for (const l of top) {
    tbody.append(h('tr', {},
      h('td', {}, l.label || '\u2014'),
      h('td', {}, l.created_at ? dateTimeEl(l.created_at, { dateOnly: true }) : '\u2014'),
      h('td', {}, l.never_expires ? h('span', { class: 'muted' }, 'Never') : (l.expires_at ? dateTimeEl(l.expires_at, { dateOnly: true }) : h('span', { class: 'muted' }, '\u2014'))),
      h('td', {}, h('span', { class: 'badge badge-ok' }, 'active'))));
  }
  if (!top.length) tbody.append(h('tr', {}, h('td', { colspan: '4', class: 'muted' }, 'No active shared links.')));
  const editBtn = h('a', { class: 'btn btn-ghost btn-sm', href: filterHref }, 'Edit');
  const card = h('div', { class: 'card', style: 'padding:0' },
    h('div', { class: 'row between', style: 'padding:18px 20px 12px' },
      h('h3', { style: 'margin:0' }, 'Active shared links'), editBtn),
    tableScroll(h('table', { class: 'table' },
      h('thead', {}, h('tr', {}, h('th', {}, 'Label'), h('th', {}, 'Created'), h('th', {}, 'Expires'), h('th', {}, 'Status'))),
      tbody)));
  if (mine.length > top.length) {
    card.append(h('div', { style: 'padding:12px 20px 18px' },
      h('a', { class: 'muted', href: filterHref }, `See more (${mine.length} total) \u2192`)));
  }
  return card;
}

function previewCard(body: HTMLElement, refresh: () => void, profile: Profile): HTMLElement {
  refresh();
  // "View Preview" opens the SAME subscriber-preview modal as the 'View' action on
  // the profiles list page (openPreview), so the owner sees exactly what a
  // subscriber sees.
  const viewPreviewBtn = h('button', { class: 'btn btn-ghost btn-sm' }, 'View Preview');
  viewPreviewBtn.addEventListener('click', () => openPreview(profile));
  return h('div', { class: 'card' },
    h('div', { class: 'row between', style: 'align-items:center' },
      h('h3', { style: 'margin:0' }, 'What a subscriber sees'), viewPreviewBtn),
    body);
}
